import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import {
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  History,
  Loader2,
  Pause,
  Pencil,
  Play,
  Plus,
  Trash2,
  Upload,
  Users,
  X,
} from "lucide-react";
import {
  ACTION_LABELS,
  NewsletterAuditEvent,
  NewsletterGroup,
  NewsletterImportResult,
  NewsletterListResponse,
  NewsletterSubscriber,
  STATUS_CLASS,
  STATUS_LABELS,
  SubscriberStatus,
} from "../../model/Newsletter";

const PER_PAGE = 25;
const base = () => `${API_URLS.NEWSLETTER_ADMIN}/admin`;

const statusOptions: { value: SubscriberStatus | ""; label: string }[] = [
  { value: "", label: "Todos os estados" },
  { value: "active", label: "Ativos" },
  { value: "pending", label: "Pendentes" },
  { value: "suspended", label: "Suspensos" },
  { value: "unsubscribed", label: "Cancelados" },
];

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500";
const buttonPrimary =
  "inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50";
const buttonSecondary =
  "inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50";

function formatDate(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("pt-PT");
}

function errorMessage(err: any): string {
  return err?.response?.data?.error || "Ocorreu um erro. Tente novamente.";
}

const NewsletterManager: React.FC = () => {
  const [subscribers, setSubscribers] = useState<NewsletterSubscriber[]>([]);
  const [groups, setGroups] = useState<NewsletterGroup[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [status, setStatus] = useState<SubscriberStatus | "">("");
  const [groupFilter, setGroupFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addName, setAddName] = useState("");
  const [addStatus, setAddStatus] = useState<SubscriberStatus>("active");
  const [addSendConfirmation, setAddSendConfirmation] = useState(false);
  const [addGroups, setAddGroups] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const [groupName, setGroupName] = useState("");

  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] =
    useState<NewsletterImportResult | null>(null);

  const [groupEditSubscriber, setGroupEditSubscriber] =
    useState<NewsletterSubscriber | null>(null);
  const [groupEditSelection, setGroupEditSelection] = useState<string[]>([]);

  const [auditSubscriber, setAuditSubscriber] =
    useState<NewsletterSubscriber | null>(null);
  const [subscriberAudit, setSubscriberAudit] = useState<
    NewsletterAuditEvent[]
  >([]);

  const [auditEvents, setAuditEvents] = useState<NewsletterAuditEvent[]>([]);
  const [auditAction, setAuditAction] = useState("");
  const [auditLoading, setAuditLoading] = useState(false);

  const requestSeq = useRef(0);

  const showSuccess = useCallback((message: string) => {
    setSuccess(message);
    setError(null);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(null), 6000);
    return () => clearTimeout(timer);
  }, [success]);

  const loadGroups = useCallback(async () => {
    try {
      const res = await api.get(`${base()}/groups`);
      setGroups(res.data?.data || []);
    } catch (e: any) {
      setError(errorMessage(e));
    }
  }, []);

  const loadSubscribers = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<NewsletterListResponse>(
        `${base()}/subscribers`,
        {
          params: {
            page,
            per_page: PER_PAGE,
            q: debouncedQuery || undefined,
            status: status || undefined,
            group: groupFilter || undefined,
          },
        },
      );
      if (seq !== requestSeq.current) return;
      setSubscribers(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (e: any) {
      if (seq !== requestSeq.current) return;
      setError(errorMessage(e));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [page, debouncedQuery, status, groupFilter]);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const res = await api.get(`${base()}/audit`, {
        params: {
          page: 1,
          per_page: PER_PAGE,
          action: auditAction || undefined,
        },
      });
      setAuditEvents(res.data?.data || []);
    } catch (e: any) {
      setError(errorMessage(e));
    } finally {
      setAuditLoading(false);
    }
  }, [auditAction]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    loadSubscribers();
  }, [loadSubscribers]);

  useEffect(() => {
    loadAudit();
  }, [loadAudit]);

  const pageCount = Math.max(1, Math.ceil(total / PER_PAGE));

  const groupOptions = useMemo(
    () => groups.map((group) => ({ value: group.id, label: group.name })),
    [groups],
  );

  const resetAddForm = () => {
    setAddEmail("");
    setAddName("");
    setAddStatus("active");
    setAddSendConfirmation(false);
    setAddGroups([]);
  };

  const handleAdd = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.post(`${base()}/subscribers`, {
        email: addEmail,
        name: addName.trim() || undefined,
        status: addStatus,
        sendConfirmation: addSendConfirmation,
        groups: addGroups,
        storeName: "Newsletter",
      });
      setAddOpen(false);
      resetAddForm();
      showSuccess("Subscritor adicionado.");
      await loadSubscribers();
      await loadGroups();
      await loadAudit();
    } catch (e: any) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (subscriber: NewsletterSubscriber) => {
    let next: SubscriberStatus = "active";
    let sendConfirmation = false;
    if (subscriber.status === "active") {
      next = "suspended";
    } else if (subscriber.status === "suspended") {
      next = "active";
    } else if (subscriber.status === "unsubscribed") {
      next = "pending";
      sendConfirmation = true;
    } else if (subscriber.status === "pending") {
      if (
        !confirm(
          `Ativar manualmente ${subscriber.email}? Isto dispensa a confirmação por email.`,
        )
      ) {
        return;
      }
      next = "active";
    }
    try {
      await api.patch(`${base()}/subscribers/${subscriber.id}`, {
        status: next,
        sendConfirmation,
        storeName: "Newsletter",
      });
      showSuccess(`Estado alterado para ${STATUS_LABELS[next].toLowerCase()}.`);
      await loadSubscribers();
      await loadAudit();
    } catch (e: any) {
      setError(errorMessage(e));
    }
  };

  const handleRenameSubscriber = async (subscriber: NewsletterSubscriber) => {
    const name = prompt("Nome do subscritor", subscriber.name || "");
    if (name === null) return;
    if ([...name.trim()].length > 120) {
      setError("O nome não pode exceder 120 caracteres.");
      return;
    }
    try {
      await api.patch(`${base()}/subscribers/${subscriber.id}`, {
        name,
      });
      showSuccess("Nome atualizado.");
      await loadSubscribers();
      await loadAudit();
    } catch (e: any) {
      setError(errorMessage(e));
    }
  };

  const handleDelete = async (subscriber: NewsletterSubscriber) => {
    if (
      !confirm(
        `Remover permanentemente ${subscriber.email}? Esta ação não pode ser revertida.`,
      )
    ) {
      return;
    }
    try {
      await api.delete(`${base()}/subscribers/${subscriber.id}`);
      showSuccess("Subscritor removido.");
      await loadSubscribers();
      await loadGroups();
      await loadAudit();
    } catch (e: any) {
      setError(errorMessage(e));
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get(`${base()}/subscribers/export`, {
        params: {
          status: status || undefined,
          group: groupFilter || undefined,
          format: "xlsx",
        },
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `newsletter-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      showSuccess("Exportação iniciada.");
      await loadAudit();
    } catch (e: any) {
      setError(errorMessage(e));
    }
  };

  const handleImport = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!importFile) return;
    setImporting(true);
    setError(null);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      const res = await api.post<NewsletterImportResult>(
        `${base()}/subscribers/import`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setImportResult(res.data);
      await loadSubscribers();
      await loadGroups();
      await loadAudit();
    } catch (e: any) {
      setError(errorMessage(e));
    } finally {
      setImporting(false);
    }
  };

  const handleCreateGroup = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      await api.post(`${base()}/groups`, { name: groupName });
      setGroupName("");
      showSuccess("Grupo criado.");
      await loadGroups();
      await loadAudit();
    } catch (e: any) {
      setError(errorMessage(e));
    }
  };

  const handleRenameGroup = async (group: NewsletterGroup) => {
    const name = prompt("Novo nome do grupo", group.name);
    if (!name || name.trim() === "" || name === group.name) return;
    try {
      await api.put(`${base()}/groups/${group.id}`, { name });
      showSuccess("Grupo atualizado.");
      await loadGroups();
      await loadAudit();
    } catch (e: any) {
      setError(errorMessage(e));
    }
  };

  const handleDeleteGroup = async (group: NewsletterGroup) => {
    if (
      !confirm(
        `Remover o grupo "${group.name}"? Os subscritores permanecem, apenas a associação é removida.`,
      )
    ) {
      return;
    }
    try {
      await api.delete(`${base()}/groups/${group.id}`);
      showSuccess("Grupo removido.");
      await loadGroups();
      await loadAudit();
    } catch (e: any) {
      setError(errorMessage(e));
    }
  };

  const openGroupEditor = (subscriber: NewsletterSubscriber) => {
    setGroupEditSubscriber(subscriber);
    setGroupEditSelection((subscriber.groups || []).map((group) => group.id));
  };

  const handleSaveGroups = async () => {
    if (!groupEditSubscriber) return;
    try {
      await api.patch(`${base()}/subscribers/${groupEditSubscriber.id}`, {
        groups: groupEditSelection,
      });
      setGroupEditSubscriber(null);
      showSuccess("Grupos atualizados.");
      await loadSubscribers();
      await loadGroups();
      await loadAudit();
    } catch (e: any) {
      setError(errorMessage(e));
    }
  };

  const openAudit = async (subscriber: NewsletterSubscriber) => {
    setAuditSubscriber(subscriber);
    setSubscriberAudit([]);
    try {
      const res = await api.get(`${base()}/subscribers/${subscriber.id}/audit`);
      setSubscriberAudit(res.data?.data || []);
    } catch (e: any) {
      setError(errorMessage(e));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Newsletter</h2>
          <p className="text-sm text-gray-500">
            {total} subscritor{total === 1 ? "" : "es"} neste tenant.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={buttonPrimary}
            onClick={() => setAddOpen(true)}
          >
            <Plus size={16} aria-hidden="true" /> Adicionar
          </button>
          <button
            type="button"
            className={buttonSecondary}
            onClick={() => {
              setImportOpen(true);
              setImportResult(null);
              setImportFile(null);
            }}
          >
            <Upload size={16} aria-hidden="true" /> Importar ficheiro
          </button>
          <button
            type="button"
            className={buttonSecondary}
            onClick={handleExport}
          >
            <Download size={16} aria-hidden="true" /> Exportar Excel
          </button>
        </div>
      </div>

      <div aria-live="polite">
        {success && (
          <div className="flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
            <CheckCircle size={16} aria-hidden="true" /> {success}
          </div>
        )}
        {error && (
          <div
            role="alert"
            className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            <AlertCircle size={16} aria-hidden="true" /> {error}
          </div>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <input
          className={inputClass}
          placeholder="Pesquisar por email"
          value={query}
          onChange={(event) => {
            setPage(1);
            setQuery(event.target.value);
          }}
          aria-label="Pesquisar por email"
        />
        <select
          className={inputClass}
          value={status}
          onChange={(event) => {
            setPage(1);
            setStatus(event.target.value as SubscriberStatus | "");
          }}
          aria-label="Filtrar por estado"
        >
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <select
          className={inputClass}
          value={groupFilter}
          onChange={(event) => {
            setPage(1);
            setGroupFilter(event.target.value);
          }}
          aria-label="Filtrar por grupo"
        >
          <option value="">Todos os grupos</option>
          {groupOptions.map((option) => (
            <option
              key={option.value}
              value={groups.find((g) => g.id === option.value)?.slug || ""}
            >
              {option.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className={buttonSecondary}
          onClick={() => {
            setPage(1);
            setQuery("");
            setStatus("");
            setGroupFilter("");
          }}
        >
          Limpar filtros
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table
          aria-label="Subscritores da newsletter"
          className="min-w-full divide-y divide-gray-200 text-sm"
        >
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <tr>
              <th scope="col" className="px-3 py-2">
                Email
              </th>
              <th scope="col" className="px-3 py-2">
                Estado
              </th>
              <th scope="col" className="px-3 py-2">
                Grupos
              </th>
              <th scope="col" className="px-3 py-2">
                Criado
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-3 py-8 text-center text-gray-500"
                  role="status"
                >
                  <Loader2
                    className="mx-auto animate-spin"
                    size={20}
                    aria-hidden="true"
                  />
                  <span className="sr-only">A carregar subscritores…</span>
                </td>
              </tr>
            ) : subscribers.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-gray-500">
                  Sem subscritores para os filtros selecionados.
                </td>
              </tr>
            ) : (
              subscribers.map((subscriber) => (
                <tr key={subscriber.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium text-gray-800">
                    {subscriber.name && (
                      <span className="block text-gray-900">
                        {subscriber.name}
                      </span>
                    )}
                    {subscriber.email}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_CLASS[subscriber.status]}`}
                    >
                      {STATUS_LABELS[subscriber.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {(subscriber.groups || []).length > 0
                      ? (subscriber.groups || [])
                          .map((group) => group.name)
                          .join(", ")
                      : "—"}
                  </td>
                  <td className="px-3 py-2 text-gray-500">
                    {formatDate(subscriber.created_at)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                        onClick={() => handleToggleStatus(subscriber)}
                        aria-label={
                          subscriber.status === "active"
                            ? `Suspender ${subscriber.email}`
                            : subscriber.status === "pending"
                              ? `Ativar ${subscriber.email}`
                              : `Reativar ${subscriber.email}`
                        }
                        title={
                          subscriber.status === "active"
                            ? "Suspender"
                            : subscriber.status === "pending"
                              ? "Ativar"
                              : "Reativar"
                        }
                      >
                        {subscriber.status === "active" ? (
                          <Pause size={16} aria-hidden="true" />
                        ) : (
                          <Play size={16} aria-hidden="true" />
                        )}
                      </button>
                      <button
                        type="button"
                        className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                        onClick={() => handleRenameSubscriber(subscriber)}
                        aria-label={`Editar nome de ${subscriber.email}`}
                        title="Editar nome"
                      >
                        <Pencil size={16} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                        onClick={() => openGroupEditor(subscriber)}
                        aria-label={`Editar grupos de ${subscriber.email}`}
                        title="Grupos"
                      >
                        <Users size={16} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                        onClick={() => openAudit(subscriber)}
                        aria-label={`Ver histórico de ${subscriber.email}`}
                        title="Histórico"
                      >
                        <History size={16} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="rounded p-1.5 text-red-500 hover:bg-red-50 hover:text-red-700"
                        onClick={() => handleDelete(subscriber)}
                        aria-label={`Remover ${subscriber.email}`}
                        title="Remover"
                      >
                        <Trash2 size={16} aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>
          Página {page} de {pageCount}
        </span>
        <div className="flex gap-1">
          <button
            type="button"
            className={buttonSecondary}
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            aria-label="Página anterior"
          >
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            className={buttonSecondary}
            disabled={page >= pageCount}
            onClick={() =>
              setPage((current) => Math.min(pageCount, current + 1))
            }
            aria-label="Página seguinte"
          >
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </div>
      </div>

      <section className="rounded-lg border border-gray-200 p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            Grupos / perfis
          </h3>
        </div>
        <form onSubmit={handleCreateGroup} className="mb-3 flex gap-2">
          <input
            className={inputClass}
            placeholder="Novo grupo (ex.: VIP, Testes)"
            value={groupName}
            onChange={(event) => setGroupName(event.target.value)}
            aria-label="Nome do novo grupo"
          />
          <button
            type="submit"
            className={buttonPrimary}
            disabled={!groupName.trim()}
          >
            <Plus size={16} aria-hidden="true" /> Criar
          </button>
        </form>
        <div className="flex flex-wrap gap-2">
          {groups.length === 0 && (
            <p className="text-sm text-gray-500">Ainda não existem grupos.</p>
          )}
          {groups.map((group) => (
            <span
              key={group.id}
              className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700"
            >
              <span className="font-medium">{group.name}</span>
              <span className="text-xs text-gray-500">
                {group.subscriber_count}
              </span>
              <button
                type="button"
                onClick={() => handleRenameGroup(group)}
                className="rounded p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-800"
                aria-label={`Renomear grupo ${group.name}`}
              >
                <Pencil size={14} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => handleDeleteGroup(group)}
                className="rounded p-1.5 text-red-500 hover:bg-red-50 hover:text-red-700"
                aria-label={`Remover grupo ${group.name}`}
              >
                <X size={14} aria-hidden="true" />
              </button>
            </span>
          ))}
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            Histórico de auditoria
          </h3>
          <select
            className={`${inputClass} max-w-xs`}
            value={auditAction}
            onChange={(event) => setAuditAction(event.target.value)}
            aria-label="Filtrar histórico por ação"
          >
            <option value="">Todas as ações</option>
            {Object.entries(ACTION_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table
            aria-label="Histórico de auditoria da newsletter"
            className="min-w-full divide-y divide-gray-200 text-sm"
          >
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              <tr>
                <th scope="col" className="px-3 py-2">
                  Data
                </th>
                <th scope="col" className="px-3 py-2">
                  Ação
                </th>
                <th scope="col" className="px-3 py-2">
                  Email
                </th>
                <th scope="col" className="px-3 py-2">
                  Ator
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {auditLoading ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-3 py-6 text-center text-gray-500"
                    role="status"
                  >
                    A carregar histórico…
                  </td>
                </tr>
              ) : auditEvents.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-3 py-6 text-center text-gray-500"
                  >
                    Sem eventos.
                  </td>
                </tr>
              ) : (
                auditEvents.map((event) => (
                  <tr key={event.id}>
                    <td className="px-3 py-2 text-gray-500">
                      {formatDate(event.created_at)}
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {ACTION_LABELS[event.action] || event.action}
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {event.email_masked || "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-500">
                      {event.actor_type}
                      {event.actor_id ? ` (${event.actor_id})` : ""}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {addOpen && (
        <Modal title="Adicionar subscritor" onClose={() => setAddOpen(false)}>
          <form onSubmit={handleAdd} className="space-y-4">
            <div>
              <label
                htmlFor="newsletter-add-email"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Email
              </label>
              <input
                id="newsletter-add-email"
                autoFocus
                type="email"
                required
                className={inputClass}
                value={addEmail}
                onChange={(event) => setAddEmail(event.target.value)}
              />
            </div>
            <div>
              <label
                htmlFor="newsletter-add-name"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Nome (opcional)
              </label>
              <input
                id="newsletter-add-name"
                className={inputClass}
                value={addName}
                onChange={(event) => setAddName(event.target.value)}
                maxLength={120}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="newsletter-add-status"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Estado
                </label>
                <select
                  id="newsletter-add-status"
                  className={inputClass}
                  value={addStatus}
                  onChange={(event) =>
                    setAddStatus(event.target.value as SubscriberStatus)
                  }
                >
                  <option value="active">Ativo</option>
                  <option value="pending">Pendente (enviar confirmação)</option>
                  <option value="suspended">Suspenso</option>
                  <option value="unsubscribed">Cancelado</option>
                </select>
              </div>
              <label className="flex items-end gap-2 pb-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={addSendConfirmation}
                  onChange={(event) =>
                    setAddSendConfirmation(event.target.checked)
                  }
                />
                Enviar email de confirmação
              </label>
            </div>
            <fieldset>
              <legend className="mb-2 text-sm font-medium text-gray-700">
                Grupos
              </legend>
              <div className="flex flex-wrap gap-3">
                {groups.length === 0 && (
                  <p className="text-sm text-gray-500">
                    Crie grupos na secção abaixo.
                  </p>
                )}
                {groups.map((group) => (
                  <label
                    key={group.id}
                    className="flex items-center gap-2 text-sm text-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={addGroups.includes(group.id)}
                      onChange={(event) => {
                        setAddGroups((current) =>
                          event.target.checked
                            ? [...current, group.id]
                            : current.filter((id) => id !== group.id),
                        );
                      }}
                    />
                    {group.name}
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className={buttonSecondary}
                onClick={() => setAddOpen(false)}
              >
                Cancelar
              </button>
              <button type="submit" className={buttonPrimary} disabled={saving}>
                {saving && (
                  <Loader2
                    size={16}
                    className="animate-spin"
                    aria-hidden="true"
                  />
                )}
                Guardar
              </button>
            </div>
          </form>
        </Modal>
      )}

      {importOpen && (
        <Modal
          title="Importar subscritores"
          onClose={() => setImportOpen(false)}
        >
          <form onSubmit={handleImport} className="space-y-4">
            <p className="text-sm text-gray-600">
              Formatos: <code>.xlsx</code> ou <code>.csv</code>. Colunas base:{" "}
              <code>email,name,status,groups</code> (nome opcional; grupos
              separados por <code>;</code>). Colunas extra, como{" "}
              <code>empresa</code>, são guardadas como atributos. Reimportar
              <strong> substitui</strong> os atributos existentes; não há merge.
              Máximo 10 MB / 50 000 linhas.
            </p>
            <input
              type="file"
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(event) =>
                setImportFile(event.target.files?.[0] || null)
              }
              aria-label="Ficheiro CSV ou Excel"
            />
            {importResult && (
              <div
                className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700"
                aria-live="polite"
              >
                <p>
                  Inseridos: <strong>{importResult.inserted}</strong> ·
                  Atualizados: <strong>{importResult.updated}</strong> ·
                  Ignorados: <strong>{importResult.skipped}</strong>
                </p>
                {(importResult.metadata_columns || []).length > 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    Atributos: {importResult.metadata_columns?.join(", ")}
                  </p>
                )}
                {importResult.errors.length > 0 && (
                  <ul className="mt-2 list-disc pl-5 text-red-700">
                    {importResult.errors.map((item) => (
                      <li key={`${item.line}-${item.email || ""}`}>
                        Linha {item.line}: {item.reason}
                        {item.email ? ` (${item.email})` : ""}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className={buttonSecondary}
                onClick={() => setImportOpen(false)}
              >
                Fechar
              </button>
              <button
                type="submit"
                className={buttonPrimary}
                disabled={!importFile || importing}
              >
                {importing && (
                  <Loader2
                    size={16}
                    className="animate-spin"
                    aria-hidden="true"
                  />
                )}
                Importar
              </button>
            </div>
          </form>
        </Modal>
      )}

      {groupEditSubscriber && (
        <Modal
          title={`Grupos de ${groupEditSubscriber.email}`}
          onClose={() => setGroupEditSubscriber(null)}
        >
          <div className="space-y-4">
            <fieldset>
              <legend className="mb-2 text-sm font-medium text-gray-700">
                Grupos
              </legend>
              <div className="flex flex-wrap gap-3">
                {groups.length === 0 && (
                  <p className="text-sm text-gray-500">
                    Ainda não existem grupos.
                  </p>
                )}
                {groups.map((group) => (
                  <label
                    key={group.id}
                    className="flex items-center gap-2 text-sm text-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={groupEditSelection.includes(group.id)}
                      onChange={(event) => {
                        setGroupEditSelection((current) =>
                          event.target.checked
                            ? [...current, group.id]
                            : current.filter((id) => id !== group.id),
                        );
                      }}
                    />
                    {group.name}
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className={buttonSecondary}
                onClick={() => setGroupEditSubscriber(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={buttonPrimary}
                onClick={handleSaveGroups}
              >
                Guardar
              </button>
            </div>
          </div>
        </Modal>
      )}

      {auditSubscriber && (
        <Modal
          title={`Histórico de ${auditSubscriber.email}`}
          onClose={() => setAuditSubscriber(null)}
        >
          <div className="max-h-96 overflow-y-auto">
            {subscriberAudit.length === 0 ? (
              <p className="text-sm text-gray-500">Sem eventos.</p>
            ) : (
              <ul className="space-y-2">
                {subscriberAudit.map((event) => (
                  <li
                    key={event.id}
                    className="rounded-lg border border-gray-200 p-2 text-sm"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-gray-800">
                        {ACTION_LABELS[event.action] || event.action}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatDate(event.created_at)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {event.actor_type}
                      {event.actor_id ? ` (${event.actor_id})` : ""}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

const Modal: React.FC<{
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ title, onClose, children }) => {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previousFocus.current = document.activeElement as HTMLElement | null;
    const node = dialogRef.current;
    if (node) {
      const first = node.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      (first ?? node).focus();
    }
    return () => {
      previousFocus.current?.focus();
    };
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== "Tab") return;
    const node = dialogRef.current;
    if (!node) return;
    const focusable = Array.from(
      node.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((element) => element.offsetParent !== null);
    if (focusable.length === 0) {
      event.preventDefault();
      node.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <h3 id={titleId} className="text-base font-semibold text-gray-800">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

export default NewsletterManager;
